import { SketchEntityChain, type OffsetCandidate } from './type';
import {
    AbstractSketchEdgeObject,
    SketchEllipseObject,
    SketchSplineObject,
    SketchStyleSplineObject,
    SketchBezierCurveClosureKind,
} from '../../mesh';
import { Vector3 } from 'three';

/**
 * 草图实体选择链排序器。
 * 
 * 说明：
 * 1. 将选择链内的实体按拓扑连接顺序排列，为后续偏移和盖子生成提供有序数据；
 * 2. 开口链从一端叶子实体开始遍历到另一端；
 * 3. 闭合链从任意实体开始顺时针遍历；
 * 4. 排序后的链内相邻实体共享一个端点。
 */
export class SketchEntitiesGroup {

    /**
     * 对选择链上的草图实体数据进行排序（入口方法）。
     * 
     * 说明：
     * 1. 通过邻接关系判断选择链是开口还是封闭；
     * 2. 开口链调用 {@link openSketchEntitiesSort}；
     * 3. 闭合链调用 {@link closedSketchEntitiesSort}；
     * 4. 单实体链（长度 ≤ 1）直接返回副本，不做排序。
     * 
     * @param chain 待排序的选择链（同一连通分量内的实体）。
     * @param planeNormal 草图参考面法向量（闭合链顺时针排序需要，开口链忽略）。
     * @returns 按拓扑顺序排列的新链。
     */
    static sketchEntitiesSort(chain: SketchEntityChain, planeNormal?: Vector3): SketchEntityChain {
        // 判空保护
        if (chain.length === 0) return [];

        // 单实体链直接返回副本（无需排序，也无邻接关系可供遍历）
        if (chain.length === 1) return [...chain];

        // 构建链内实体邻接表：entity uuid → 相邻实体 uuid 集合
        const adjacency = this.buildAdjacency(chain);

        // 判断链是否为闭合
        const isClosed = this.isClosedChain(chain, adjacency);

        if (isClosed) {
            return this.closedSketchEntitiesSort(chain, adjacency, planeNormal);
        } else {
            return this.openSketchEntitiesSort(chain, adjacency);
        }
    }

    // ═══════════════════════════════════════════════════════════
    // 私有工具方法
    // ═══════════════════════════════════════════════════════════

    /**
     * 构建链内实体的邻接表。
     * 
     * 说明：
     * 1. 对链内每个实体，通过其端点查找链内与之相连的其他实体；
     * 2. 两个实体"相连"定义为它们共享同一个端点 SketchPointObject 实例；
     * 3. 使用 Set 对邻居去重（两实体可能共享多个端点，但邻接表只记一次）。
     * 
     * @param chain 选择链。
     * @returns entity uuid → 相邻实体 uuid 集合的映射。
     */
    private static buildAdjacency(chain: SketchEntityChain): Map<string, Set<string>> {
        const adjacency = new Map<string, Set<string>>();
        // 链内实体 uuid 集合，用于快速过滤外部实体
        const chainSet = new Set(chain.map(e => e.uuid));

        for (const entity of chain) {
            const neighbors = new Set<string>();

            // SketchCollectionObject / SketchSlotObject 等非边实体没有端点拓扑，跳过
            if (entity instanceof AbstractSketchEdgeObject) {
                // 获取当前实体的所有关联草图点
                const points = entity.getRelatedSketchPointsSnapshot();
                for (const point of points) {
                    // 只考虑端点角色（排除中点、圆心、象限点等非拓扑连接点）
                    if (!point.isEndPoint) continue;
                    // 通过端点查找相连的边实体（O(1) 邻接表查询）
                    const connectedEdges = point.getConnectedEdgesSnapshot();
                    for (const edge of connectedEdges) {
                        // 排除自身，且只保留链内实体
                        if (edge.uuid !== entity.uuid && chainSet.has(edge.uuid)) {
                            neighbors.add(edge.uuid);
                        }
                    }
                }
            }

            adjacency.set(entity.uuid, neighbors);
        }

        return adjacency;
    }

    /**
     * 判断选择链是否为闭合链。
     * 
     * 说明：
     * 1. 多实体链：若每个实体在链内恰好有 2 个邻居，则为闭合链；
     * 2. 双实体链：若两实体共享两个端点（形成闭环），即使邻接计数各为 1 也视为闭合；
     * 3. 单实体链：若实体本身是闭合图形（圆、闭合样条、完整椭圆），则为闭合链。
     * 
     * @param chain 选择链。
     * @param adjacency 邻接表。
     * @returns 闭合返回 true，开口返回 false。
     */
    private static isClosedChain(
        chain: SketchEntityChain,
        adjacency: Map<string, Set<string>>,
    ): boolean {
        if (chain.length === 0) return false;

        // 单实体链：实体本身是否闭合
        if (chain.length === 1) {
            return this.isClosedEntity(chain[0]);
        }

        // 统计各实体的邻居数量
        let count1 = 0;
        let count2 = 0;

        for (const entity of chain) {
            const n = adjacency.get(entity.uuid)?.size ?? 0;
            if (n === 1) {
                count1++;
            } else if (n === 2) {
                count2++;
            } else {
                // 孤立实体（邻居数为 0）或邻居数异常的退化情况 → 非闭合
                return false;
            }
        }

        // 所有实体都有 2 个邻居 → 标准闭合环
        if (count2 === chain.length) return true;

        // 双实体链，各有一个邻居 → 检查是否共享两个端点（即形成闭环）
        if (chain.length === 2 && count1 === 2) {
            const [a, b] = chain;
            const aEndpoints = this.getEndpointUuids(a);
            const bEndpoints = this.getEndpointUuids(b);
            const sharedCount = aEndpoints.filter(id => bEndpoints.includes(id)).length;
            return sharedCount >= 2;
        }

        // 正好两个叶子（count1 === 2）且其余为 2 → 标准开口链
        return false;
    }

    /**
     * 判断单个实体是否为闭合图形。
     * 
     * 说明：
     * 1. 圆始终闭合；
     * 2. 样条/样式样条通过 closureKind 判断；
     * 3. 椭圆通过起止角差是否等于 2π 判断。
     * 
     * @param entity 草图实体。
     * @returns 闭合返回 true。
     */
    private static isClosedEntity(entity: OffsetCandidate): boolean {
        const entityType = entity.objectType;

        // 圆始终闭合
        if (entityType === 'Circle') return true;

        // 样条 / 样式样条：检查闭合语义
        if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
            return entity.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed;
        }

        // 椭圆：检查是否为完整椭圆（起止角差 ≈ 2π）
        if (entity instanceof SketchEllipseObject) {
            const def = entity.getEllipseDefinition();
            return Math.abs(def.endAngle - def.startAngle - 2 * Math.PI) < 1e-6;
        }

        // 其余类型（线、弧、抛物线、槽口等）均为开口
        return false;
    }

    /**
     * 获取实体的端点 UUID 列表。
     * 
     * 说明：从实体的关联草图点中筛选 isEndPoint 为 true 的点，提取其 uuid。
     * 
     * @param entity 草图实体。
     * @returns 端点 SketchPointObject 的 UUID 数组。
     */
    private static getEndpointUuids(entity: OffsetCandidate): string[] {
        // 非边实体（如 SketchCollectionObject）没有端点拓扑，返回空
        if (!(entity instanceof AbstractSketchEdgeObject)) return [];
        return entity.getRelatedSketchPointsSnapshot()
            .filter(p => p.isEndPoint)
            .map(p => p.uuid);
    }

    // ═══════════════════════════════════════════════════════════
    // 排序实现
    // ═══════════════════════════════════════════════════════════

    /**
     * 对开口的选择链草图实体数据进行排序。
     * 
     * 排序规则：
     * 1. 找到叶子实体（链内仅有一个邻居的实体，即链的一端）作为遍历起点；
     * 2. 从起点沿邻接关系逐实体遍历到另一端；
     * 3. 确保最终序列中相邻实体共享端点，为后续盖子插入提供正确的相邻关系。
     * 
     * @param chain 待排序的开口选择链。
     * @param adjacency 邻接表。
     * @returns 按连接顺序排列的新链。
     */
    private static openSketchEntitiesSort(
        chain: SketchEntityChain,
        adjacency: Map<string, Set<string>>,
    ): SketchEntityChain {
        // 找到叶子实体（邻居数 ≤ 1）作为遍历起点
        const start = chain.find(e => (adjacency.get(e.uuid)?.size ?? 0) <= 1);
        if (!start) {
            // 退路：理论上开口链必然存在叶子，若找不到则返回原链副本
            return [...chain];
        }

        return this.traverseChain(chain, adjacency, start);
    }

    /**
     * 对封闭的选择链草图实体数据进行排序。
     * 
     * 排序规则：
     * 1. 从任意实体开始，沿邻接关系遍历所有实体；
     * 2. 若提供了参考面法向量，确保遍历方向为顺时针（用于盖子方向和偏移方向判定）；
     * 3. 遍历覆盖全部实体后终止（不回到起点，避免重复）。
     * 
     * @param chain 待排序的闭合选择链。
     * @param adjacency 邻接表。
     * @param planeNormal 草图参考面法向量（可选，用于顺时针方向校正）。
     * @returns 按顺时针连接顺序排列的新链。
     */
    private static closedSketchEntitiesSort(
        chain: SketchEntityChain,
        adjacency: Map<string, Set<string>>,
        planeNormal?: Vector3,
    ): SketchEntityChain {
        // 从第一个实体开始沿邻接关系遍历
        const sorted = this.traverseChain(chain, adjacency, chain[0]);

        // 若提供了参考面法向量且链长 ≥ 2，校正为顺时针方向
        if (planeNormal && sorted.length >= 2) {
            return this.ensureClockwise(sorted, planeNormal);
        }

        return sorted;
    }

    /**
     * 沿邻接表遍历链内所有实体，生成有序序列。
     * 
     * 说明：
     * 1. 从 startEntity 出发，每次选取一个未访问的邻居作为下一步；
     * 2. 对开口链，startEntity 应为叶子实体，遍历自然单向走到另一端；
     * 3. 对闭合链，startEntity 为任意实体，遍历覆盖全部实体后终止。
     * 
     * @param chain 原始链（提供 uuid → 实体实例的查找能力）。
     * @param adjacency 邻接表。
     * @param startEntity 遍历起点。
     * @returns 按遍历顺序排列的实体数组（新数组，不修改原链）。
     */
    private static traverseChain(
        chain: SketchEntityChain,
        adjacency: Map<string, Set<string>>,
        startEntity: OffsetCandidate,
    ): SketchEntityChain {
        const result: SketchEntityChain = [startEntity];
        const visited = new Set<string>([startEntity.uuid]);
        let current = startEntity;

        // 逐实体遍历，每次从当前实体的邻居中选取一个未访问的继续
        while (result.length < chain.length) {
            const neighbors = adjacency.get(current.uuid) ?? new Set<string>();
            let next: OffsetCandidate | null = null;

            // 找到第一个未访问的邻居
            for (const nid of neighbors) {
                if (!visited.has(nid)) {
                    next = chain.find(e => e.uuid === nid) ?? null;
                    break;
                }
            }

            // 无未访问邻居则终止（正常闭合链此时已覆盖全部实体；开口链走到尽头）
            if (!next) break;

            result.push(next);
            visited.add(next.uuid);
            current = next;
        }

        return result;
    }

    /**
     * 确保闭合链的遍历方向为顺时针。
     * 
     * 说明：
     * 1. 提取相邻实体间的共享端点世界坐标，构成多边形顶点序列；
     * 2. 将 3D 顶点投影到参考面 2D 坐标系；
     * 3. 使用 Shoelace 公式计算有向面积；
     * 4. 若面积为正（表示当前序列为逆时针 CCW），反转序列以得顺时针 CW 顺序。
     * 
     * @param sorted 已按邻接遍历排序的链。
     * @param planeNormal 草图参考面法向量。
     * @returns 顺时针方向排列的链。
     */
    private static ensureClockwise(
        sorted: SketchEntityChain,
        planeNormal: Vector3,
    ): SketchEntityChain {
        // 提取相邻实体间的共享端点位置作为多边形顶点
        const vertices = this.extractSharedEndpoints(sorted);

        // 少于 3 个顶点无法判断有向面积方向（如双实体闭合环），返回原序
        if (vertices.length < 3) {
            return sorted;
        }

        // 将 3D 顶点投影到参考面 2D 坐标系
        const projected = this.projectTo2D(vertices, planeNormal);

        // 计算有向面积：正 → CCW，负 → CW
        const area = this.computeSignedArea(projected);

        // 若为 CCW（面积 > 0），反转序列以得 CW
        if (area > 0) {
            return [...sorted].reverse();
        }

        return sorted;
    }

    /**
     * 提取有序链中相邻实体间的共享端点世界坐标。
     * 
     * 说明：
     * 1. 遍历相邻实体对（包括末实体与首实体，形成闭环多边形）；
     * 2. 查找每对实体间的共享端点，取其世界坐标作为多边形顶点；
     * 3. 若某对实体无共享端点（数据异常），跳过该顶点。
     * 
     * @param sorted 已排序的链。
     * @returns 共享端点世界坐标数组。
     */
    private static extractSharedEndpoints(sorted: SketchEntityChain): Vector3[] {
        const vertices: Vector3[] = [];
        const n = sorted.length;

        for (let i = 0; i < n; i++) {
            const entityA = sorted[i];
            // 下一实体，末实体后回到首实体（闭合环）
            const entityB = sorted[(i + 1) % n];

            const pos = this.getSharedEndpointPosition(entityA, entityB);
            if (pos) {
                vertices.push(pos);
            }
        }

        return vertices;
    }

    /**
     * 获取两个实体共享端点的世界坐标。
     * 
     * 说明：
     * 1. 分别获取两实体的端点列表，通过 UUID 匹配找到共享端点；
     * 2. 共享端点意味着两个实体在拓扑上通过同一个 SketchPointObject 相连。
     * 
     * @param entityA 实体 A。
     * @param entityB 实体 B。
     * @returns 共享端点的世界坐标，若无共享端点返回 null。
     */
    private static getSharedEndpointPosition(
        entityA: OffsetCandidate,
        entityB: OffsetCandidate,
    ): Vector3 | null {
        // 非边实体（如 SketchCollectionObject）没有端点拓扑，返回 null
        if (!(entityA instanceof AbstractSketchEdgeObject) || !(entityB instanceof AbstractSketchEdgeObject)) {
            return null;
        }
        const aPoints = entityA.getRelatedSketchPointsSnapshot().filter(p => p.isEndPoint);
        const bPoints = entityB.getRelatedSketchPointsSnapshot().filter(p => p.isEndPoint);

        for (const ap of aPoints) {
            for (const bp of bPoints) {
                if (ap.uuid === bp.uuid) {
                    return ap.getWorldPosition(new Vector3());
                }
            }
        }

        return null;
    }

    /**
     * 将 3D 点集投影到参考面的 2D 坐标系。
     * 
     * 说明：
     * 1. 以 planeNormal 为法向量，构造两个正交基向量 u、v；
     * 2. u = cross(planeNormal, worldUp)，若法向量平行于 worldUp 则改用 X 轴；
     * 3. v = cross(u, planeNormal)，保证 (u, v, planeNormal) 构成右手系；
     * 4. 每个 3D 点的 2D 坐标为 (dot(p, u), dot(p, v))。
     * 
     * @param points 3D 点数组。
     * @param planeNormal 参考面法向量（需已归一化）。
     * @returns 2D 点数组 [{x, y}, ...]。
     */
    private static projectTo2D(
        points: Vector3[],
        planeNormal: Vector3,
    ): { x: number; y: number }[] {
        // 构造正交基：u ⊥ planeNormal
        const worldUp = new Vector3(0, 1, 0);
        const u = new Vector3().crossVectors(planeNormal, worldUp);

        // 法向量平行于世界向上时（如顶面/底面），换 X 轴作参考方向
        if (u.lengthSq() < 1e-10) {
            u.crossVectors(planeNormal, new Vector3(1, 0, 0));
        }
        u.normalize();

        // v 与 u、planeNormal 正交，构成右手系
        const v = new Vector3().crossVectors(u, planeNormal).normalize();

        // 投影各点：2D 坐标 = (dot(p, u), dot(p, v))
        return points.map(p => ({
            x: p.dot(u),
            y: p.dot(v),
        }));
    }

    /**
     * 计算 2D 多边形的有向面积（Shoelace 公式）。
     * 
     * 说明：
     * - 面积 > 0：顶点顺序为逆时针（CCW）；
     * - 面积 < 0：顶点顺序为顺时针（CW）；
     * - 面积 ≈ 0：退化多边形（共线或重叠点）。
     * 
     * @param vertices 2D 顶点数组。
     * @returns 有向面积（未乘 0.5，仅符号对方向判定有意义）。
     */
    private static computeSignedArea(vertices: { x: number; y: number }[]): number {
        let area = 0;
        const n = vertices.length;

        for (let i = 0; i < n; i++) {
            const curr = vertices[i];
            const next = vertices[(i + 1) % n];
            area += curr.x * next.y - next.x * curr.y;
        }

        return area; // 实际有向面积 = area / 2，此处只关心符号
    }
}
